<template>
	<div class="PanelCDKDealInfo">
		<IsBusySectionComponent
			v-if="isBusy"
			text="Please wait while we locate this deal..."
			delayToShowText="100"
		/>
		<template v-else-if="hasExternalDMSData">
			<div class="grid-header">
				<InputTextbox
					v-model:saturn="searchText"
					placeholder="Search"
					label="Search"
					class="search-input"
					@change="() => SearchHelper.resetPageSize()"
				/>
			</div>

			<div class="grid-container">
				<div
					class="grid-item"
					v-for="(item, index) in filteredItems"
					:key="index"
				>
					<label>{{ item.label }}</label>
					<input
						readonly
						:value="item.value"
					/>
				</div>
				<div
					class="auto-scroller"
					v-seen="(observer: IntersectionObserver) => incrementItemsToShow(observer)"
				/>
			</div>

			<!-- <AccordionList
                :list="categorizedCdkValues"
                listLabel="label"
            >
                <template #listBody="{ bodyItem: category, index: categoryIndex }">
                    <json-viewer
                        :value="category.values"
                        :expand-depth="1"
                        copyable
                        boxed
                    >
                        <template #copy>
                            <span class="copy-icon">
                                <i class="far fa-clone" />
                            </span>
                        </template>
                    </json-viewer>
                </template>
            </AccordionList> -->
		</template>
		<template v-else>
			<h3>This deal is not found in an external DMS.</h3>
		</template>
		<button
			:disabled="isBusy"
			@click="setCDKDealInfo(true)"
		>
			Refresh External Deal Info
		</button>
	</div>
</template>

<script lang="ts">
	export interface IPanelCDKDealInfoProps {
		fimenu: FIMenu;
	}
</script>

<script setup lang="ts">
	//#region Imports

	import { computed, onBeforeMount, onMounted, Ref, ref } from 'vue';
	import DealDMSData from '@core/classes/Deal/DealDmsData';
	import SearchableObjectHelper from '@/helpers/searchable-object-helper';

	import api from '@core/services/api/_api';
	import FIMenu from '@core/classes/FIMenu';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import SaturnResponse from '@core/classes/Responses/SaturnResponse';
	import util from '@core/services/util';
	////#endregion

	//#region Props
	const { fimenu } = defineProps<IPanelCDKDealInfoProps>();
	//#endregion

	//#region States
	const isBusy = ref<boolean>(false);
	const searchText = ref<string>('');
	const SearchHelper = ref<SearchableObjectHelper>(null) as Ref<SearchableObjectHelper>;
	const dealsExternalDMSData = ref<DealDMSData>(null) as Ref<DealDMSData>;
	//#endregion

	//#region Lifecycle Hooks
	onBeforeMount(() => {
		setCDKDealInfo();
	});

	onMounted(() => {});
	//#endregion

	//#region Computed
	/**
	 * Gets the DBData object from the FIMenu.
	 */
	const dbData = computed(() => {
		return dealsExternalDMSData.value?.dbData;
	});

	/**
	 * Checks if the deal has external DMS data.
	 */
	const hasExternalDMSData = computed<boolean>(() => {
		return dbData.value != null;
	});

	/**
	 * Filters the items.
	 */
	const filteredItems = computed(() => {
		const filtered = SearchHelper.value.handleSearch(searchText.value);

		return Object.entries(filtered).map(([key, value]) => ({
			label: key,
			value: value,
		}));
	});

	//#endregion

	//#region Methods

	/**
	 * Increments the amount of items to show.
	 * @param observer - The observer to disconnect from.
	 */
	function incrementItemsToShow(observer: IntersectionObserver) {
		SearchHelper.value.incrementPageSize();
	}

	/**
	 * Sets the CDK deal info. Fetches the deal from CDK and saves it to the FIMenu.
	 */
	async function setCDKDealInfo(getNew: boolean = false) {
		if (fimenu.dmsData && !getNew) {
			dealsExternalDMSData.value = fimenu.dmsData;
		} else {
			try {
				isBusy.value = true;

				if (fimenu.externalDMSDealNumber == null) {
					throw Error('This deal is missing a DMS #.');
				}
				const { data } = await api.fimenu.getExternalDMSDealData(fimenu, getNew);
				const { payload, messages, statusCode } = data as SaturnResponse<DealDMSData>;
				if (statusCode !== 200)
					throw Error(messages.join(', '), {
						cause: {
							statusCode,
							messages,
						},
					});

				if (payload) {
					dealsExternalDMSData.value = payload;
					fimenu.setDMSData(payload);
					if (fimenu.externalDMSDealSaturnId != payload.id) {
						fimenu.setExternalDMSDealSaturnId(payload.id);
					}
				}
			} catch (error) {
				console.error(error);
				util.toastr('error', 'Retrieving DMS Deal Info', String(error));
				return;
			} finally {
				isBusy.value = false;
			}
		}

		SearchHelper.value = new SearchableObjectHelper(dbData.value);
	}

	// /**
	//  * Formats a string to a more readable label.
	//  * @param str - The string to format.
	//  * @returns The formatted string.
	//  */
	// function formatLabel(str: string) {
	//     if (typeof str !== 'string') return;

	//     const separateCamelCase = (str: string) => str.replace(/([a-z])([A-Z])/g, '$1 $2');
	//     const capitalizeFirstLetter = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
	//     const replaceMgrWithManager = (text: string) => text.replace(/\bMgr\b/g, 'Manager');

	//     str = separateCamelCase(str);
	//     str = capitalizeFirstLetter(str);
	//     str = replaceMgrWithManager(str);
	//     return str;
	// }

	// /**
	//  * Categorizes the CDK deal info.
	//  */
	// const categorizedCdkValues = computed(() => {
	//     const categorizedValues: any[] = [];
	//     if (!originalDbData.value) return categorizedValues;

	//     for (let key in originalDbData.value) {
	//         let category = {
	//             label: formatLabel(key),
	//             values: originalDbData.value[key as keyof typeof originalDbData.value],
	//         };

	//         categorizedValues.push(category);
	//     }
	//     return categorizedValues;
	// });

	//#endregion
</script>
<style>
	.PanelCDKDealInfo {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: center;
		gap: 25px;
		height: 100%;
		padding: 10px;
		box-sizing: border-box;
		overflow: auto;
		overflow-x: hidden;
	}
	.PanelCDKDealInfo > button {
		width: fit-content;
	}

	.PanelCDKDealInfo .grid-container {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		grid-gap: 25px;
		width: 100%;
		overflow: auto;
		max-height: 45vh;
		padding-right: 5px;
	}

	.PanelCDKDealInfo .grid-container .grid-item {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-start;
	}

	.PanelCDKDealInfo .grid-container .grid-item input {
		width: 100%;
		height: 50%;
	}

	.PanelCDKDealInfo .grid-header {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}

	.PanelCDKDealInfo .grid-header .search-input {
		width: 25%;
		min-width: 200px;
	}

	.PanelCDKDealInfo .IsBusySectionComponent-container {
		height: unset !important;
	}
	.PanelCDKDealInfo .auto-scroller {
		height: 25px;
		visibility: hidden;
	}
</style>
